import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Card, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { chatService } from "@/services/ChatService";
import { getRootStore } from "@/stores/RootStore";
import { ChatMapper, MessageAuthorType } from "@/types/chat";
import { onError } from "@/utils/onError";
import { supabase } from "@/utils/supabase";
import styles from "@/styles/OrderDetail/ClientChatStream.module.less";
const { Text } = Typography;
async function fetchOrderThreadRow(orderId) {
    const { data, error } = await supabase()
        .from("mm_order_thread")
        .select("mm_channel_id, mm_root_post_id")
        .eq("order_id", orderId)
        .maybeSingle();
    if (error) {
        // Not found is a normal case — return null silently
        return null;
    }
    return data;
}
async function fetchSupportChannelId(clientId) {
    const { data, error } = await supabase()
        .from("mm_channel_mapping")
        .select("mm_channel_id")
        .eq("channel_type", "support")
        .eq("client_id", clientId)
        .maybeSingle();
    if (error) {
        return null;
    }
    return data?.mm_channel_id ?? null;
}
// ---------------------------------------------------------------------------
// Message fetching
// ---------------------------------------------------------------------------
async function fetchClientMessages(orderId, clientId, currentUserId, botUserId) {
    const messages = [];
    // --- Thread 1: order thread in route channel ---
    try {
        const threadRow = await fetchOrderThreadRow(orderId);
        if (threadRow?.mm_root_post_id) {
            const postList = await chatService.getPostThread(threadRow.mm_root_post_id);
            for (const postId of postList.order) {
                const post = postList.posts[postId];
                if (!post)
                    continue;
                const authorType = ChatMapper.getAuthorType(post, currentUserId, botUserId);
                if (authorType === MessageAuthorType.CLIENT) {
                    messages.push(ChatMapper.postToDomain(post, "Клиент", authorType));
                }
            }
        }
    }
    catch {
        // Order thread not found or MM unavailable — degrade gracefully
    }
    // --- Thread 2: support channel ---
    if (clientId) {
        try {
            const supportChannelId = await fetchSupportChannelId(clientId);
            if (supportChannelId) {
                const postList = await chatService.getPosts(supportChannelId);
                for (const postId of postList.order) {
                    const post = postList.posts[postId];
                    if (!post)
                        continue;
                    const authorType = ChatMapper.getAuthorType(post, currentUserId, botUserId);
                    if (authorType === MessageAuthorType.CLIENT) {
                        messages.push(ChatMapper.postToDomain(post, "Клиент", authorType));
                    }
                }
            }
        }
        catch {
            // Support channel not found — degrade gracefully
        }
    }
    // Merge and sort by creation timestamp ascending
    messages.sort((a, b) => a.createAt - b.createAt);
    // Deduplicate by message id (order thread might overlap support channel)
    const seen = new Set();
    return messages.filter((m) => {
        if (seen.has(m.id))
            return false;
        seen.add(m.id);
        return true;
    });
}
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const formatTime = (ts) => {
    return new Date(ts).toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};
const ClientChatStream = observer(() => {
    const { orderDetailStore, chatStore } = getRootStore();
    const details = orderDetailStore.details;
    const orderId = details?.id ?? null;
    const clientId = details?.idClient ?? null;
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    // Track which channels/threads we're watching for realtime updates
    const watchedChannelIds = useRef(new Set());
    const watchedThreadRootIds = useRef(new Set());
    useEffect(() => {
        if (!orderId)
            return;
        let cancelled = false;
        const load = async () => {
            // Ensure chat is initialized (idempotent — returns immediately if already connected)
            try {
                await chatStore.initChat();
            }
            catch {
                // MM not available — show empty state
            }
            if (!chatStore.isConnected) {
                // Chat not available — show empty state
                return;
            }
            setIsLoading(true);
            try {
                const currentUserId = chatStore.currentUserId ?? "";
                const botUserId = chatStore.botUserId ?? "";
                const loaded = await fetchClientMessages(orderId, clientId, currentUserId, botUserId);
                if (!cancelled) {
                    setMessages(loaded);
                    // Track channels for realtime
                    watchedChannelIds.current.clear();
                    watchedThreadRootIds.current.clear();
                    // Populate watched channel/thread ids for WS filtering
                    try {
                        const threadRow = await fetchOrderThreadRow(orderId);
                        if (threadRow) {
                            watchedChannelIds.current.add(threadRow.mm_channel_id);
                            watchedThreadRootIds.current.add(threadRow.mm_root_post_id);
                        }
                    }
                    catch {
                        // pass
                    }
                    if (clientId) {
                        try {
                            const supportChannelId = await fetchSupportChannelId(clientId);
                            if (supportChannelId) {
                                watchedChannelIds.current.add(supportChannelId);
                            }
                        }
                        catch {
                            // pass
                        }
                    }
                }
            }
            catch (error) {
                if (!cancelled) {
                    onError(error, "Ошибка загрузки сообщений клиента");
                }
            }
            finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [orderId, clientId]);
    // Realtime WS handler — subscribe to new/edited posts in watched channels
    useEffect(() => {
        if (!chatStore.isConnected)
            return;
        const currentUserId = chatStore.currentUserId ?? "";
        const botUserId = chatStore.botUserId ?? "";
        const wsHandler = (msg) => {
            if (msg.event !== "posted" && msg.event !== "post_edited")
                return;
            const data = msg.data;
            if (!data?.post)
                return;
            let post;
            try {
                post = JSON.parse(data.post);
            }
            catch {
                return;
            }
            // Only care about our watched channels
            if (!watchedChannelIds.current.has(post.channel_id))
                return;
            // Only keep CLIENT messages
            const authorType = ChatMapper.getAuthorType(post, currentUserId, botUserId);
            if (authorType !== MessageAuthorType.CLIENT)
                return;
            const newMsg = ChatMapper.postToDomain(post, "Клиент", authorType);
            if (msg.event === "posted") {
                setMessages((prev) => {
                    // Avoid duplicates
                    if (prev.some((m) => m.id === newMsg.id))
                        return prev;
                    return [...prev, newMsg].sort((a, b) => a.createAt - b.createAt);
                });
            }
            else if (msg.event === "post_edited") {
                setMessages((prev) => prev.map((m) => m.id === newMsg.id ? { ...m, message: newMsg.message } : m));
            }
        };
        // Register our handler — chatService.offMessage removes only THIS callback on cleanup
        try {
            chatService.onMessage(wsHandler);
        }
        catch {
            // WS not connected — no realtime, but that's acceptable
            return;
        }
        return () => {
            // Remove ONLY our handler — do NOT disconnect the shared WS
            chatService.offMessage(wsHandler);
        };
    }, [chatStore.isConnected, chatStore.currentUserId, chatStore.botUserId]);
    return (_jsxs(Card, { className: styles.card, size: "small", children: [_jsx("div", { className: styles.sectionTitle, children: "\u0421\u043E\u043E\u0431\u0449\u0435\u043D\u0438\u044F \u043A\u043B\u0438\u0435\u043D\u0442\u0430" }), isLoading ? (_jsx("div", { className: styles.loadingWrapper, children: _jsx(Spin, { size: "small" }) })) : messages.length === 0 ? (_jsx(Text, { className: styles.emptyState, children: "\u0421\u043E\u043E\u0431\u0449\u0435\u043D\u0438\u0439 \u043A\u043B\u0438\u0435\u043D\u0442\u0430 \u043D\u0435\u0442" })) : (_jsx("div", { className: styles.stream, children: messages.map((msg) => (_jsxs("div", { className: styles.messageItem, children: [_jsx("div", { className: styles.messageBubble, children: msg.message }), _jsxs("div", { className: styles.messageMeta, children: [_jsx("span", { children: msg.authorName }), _jsx("span", { children: "\u00B7" }), _jsx("span", { children: formatTime(msg.createAt) })] })] }, msg.id))) }))] }));
});
ClientChatStream.displayName = "ClientChatStream";
export default ClientChatStream;
